import { IS_NATIVE } from '~/utils/platform'
import { PROGRESS_KEYS } from '~/utils/progressEpoch'

/** localStorage is the synchronous hot store; one Preferences record is the
 * atomic recovery snapshot. Identity/revision metadata travels with the runs,
 * never independently. Auth tokens remain in Firebase's IndexedDB persistence. */
export const NATIVE_MIRROR_KEY = 'ed-native-progress-v1'
export const NATIVE_HAD_UID_KEY = 'ed-native-had-uid-v1'
export const NATIVE_MIRROR_READY_KEY = 'elemental-dispatch-native-mirror-ready'
export const NATIVE_SIGNIN_NUDGE_KEY = 'elemental-dispatch-native-signin-nudge'
const criticalKeys = new Set([
  ...PROGRESS_KEYS,
  'elemental-dispatch-epoch', 'elemental-dispatch-device-id',
  'elemental-dispatch-signed-in', NATIVE_SIGNIN_NUDGE_KEY,
])
export const isNativeMirrorKey = (key: string) => criticalKeys.has(key)
  || key.startsWith('elemental-dispatch-run-parked:')

type PreferencesStore = {
  get(options: { key: string }): Promise<{ value: string | null }>
  set(options: { key: string; value: string }): Promise<void>
  remove(options: { key: string }): Promise<void>
}
let preferences: Promise<PreferencesStore> | undefined
// Capacitor plugins are proxies: returning the proxy from a Promise callback
// makes Promise assimilation call its synthetic native `then` method. Return a
// plain adapter instead; only actual plugin methods cross the bridge.
const store = () => preferences ??= import('@capacitor/preferences').then(({ Preferences }) => ({
  get: (options: { key: string }) => Preferences.get(options),
  set: (options: { key: string; value: string }) => Preferences.set(options),
  remove: (options: { key: string }) => Preferences.remove(options),
}))
let restored: Promise<void> | undefined
let queue: Promise<void> = Promise.resolve()
let writingBlocked = false
let dirty = false
let scheduled = false
let hadUid: boolean | undefined
let uidMarkerRevision = 0
let pendingUidReservation: number | null = null
let confirmedUidObserved = false

function snapshot(): string {
  const entries: Record<string, string> = {}
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key || !isNativeMirrorKey(key)) continue
    const value = localStorage.getItem(key)
    if (value !== null) entries[key] = value
  }
  return JSON.stringify({ version: 1, entries })
}

function serialize(operation: () => Promise<void>): Promise<void> {
  const next = queue.catch(() => {}).then(operation)
  queue = next
  return next
}

/** Runs before the epoch plugin and all synchronous save readers. A healthy hot
 * store wins, including intentionally missing keys. Only a missing installation
 * stamp means WebView storage was evicted; restore the snapshot as a unit then. */
export function restoreNativePersistence(): Promise<void> {
  if (!IS_NATIVE) return Promise.resolve()
  return restored ??= (async () => {
    try {
      const prefs = await store()
      const saved = await prefs.get({ key: NATIVE_MIRROR_KEY })
      if (!localStorage.getItem(NATIVE_MIRROR_READY_KEY) && saved.value) {
        const parsed = JSON.parse(saved.value) as { version?: unknown; entries?: Record<string, unknown> }
        if (parsed.version !== 1 || !parsed.entries || typeof parsed.entries !== 'object'
          || Array.isArray(parsed.entries)) throw new Error('Native save recovery is unavailable')
        // Reject corrupt values before changing any hot-store key.
        const entries = Object.entries(parsed.entries).filter(([key]) => isNativeMirrorKey(key))
        if (entries.some(([, value]) => typeof value !== 'string')) throw new Error('Native save recovery is unavailable')
        for (const [key, value] of entries) localStorage.setItem(key, value as string)
      }
    } catch (error) { reportFailure(error) }
    // Recovery is best-effort: keep the hot store and finish startup even when
    // the native copy cannot be read. A failed first flush stays dirty to retry.
    try {
      localStorage.setItem(NATIVE_MIRROR_READY_KEY, '1')
      installWriteBehind()
      dirty = true
      await flushNativePersistence()
    } catch (error) { reportFailure(error) }
  })()
}

/** Intercept only our explicit key whitelist on localStorage. This keeps the
 * existing synchronous readers/writers, and covers removals, cloud adoption,
 * parked runs and progress resets as well as normal persistence heartbeats. */
function installWriteBehind() {
  const prototype = Object.getPrototypeOf(localStorage) as Storage
  const setItem = prototype.setItem
  const removeItem = prototype.removeItem
  const clear = prototype.clear
  prototype.setItem = function (key: string, value: string) {
    setItem.call(this, key, value)
    if (this === localStorage && isNativeMirrorKey(String(key))) changed()
  }
  prototype.removeItem = function (key: string) {
    removeItem.call(this, key)
    if (this === localStorage && isNativeMirrorKey(String(key))) changed()
  }
  prototype.clear = function () {
    clear.call(this)
    if (this === localStorage) changed()
  }
  window.addEventListener('pagehide', () => { void flushNativePersistence().catch(reportFailure) })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flushNativePersistence().catch(reportFailure)
  })
}

function reportFailure(error: unknown) {
  console.warn('Native save mirror unavailable; local progress is retained.', error)
}

function changed() {
  if (writingBlocked) return
  dirty = true
  if (scheduled) return
  scheduled = true
  // Coalesce all writes in one synchronous progress/identity transaction.
  queueMicrotask(() => {
    scheduled = false
    void flushNativePersistence().catch(reportFailure)
  })
}

/** Resets/deletion await this barrier before navigation. Failed writes remain
 * dirty and can be retried; older in-flight writes cannot overtake a clear. */
export function flushNativePersistence(): Promise<void> {
  if (!IS_NATIVE || writingBlocked || !dirty) return queue
  dirty = false
  const value = snapshot()
  return serialize(async () => {
    try { await (await store()).set({ key: NATIVE_MIRROR_KEY, value }) }
    catch (error) { dirty = true; throw error }
  })
}

/** A missing native auth session after a previous UID is a recovery flow, never
 * permission to silently create another guest wallet. Read errors fail closed. */
export async function nativeHadUid(): Promise<boolean> {
  if (!IS_NATIVE) return false
  if (hadUid !== undefined) return hadUid
  try {
    const saved = await (await store()).get({ key: NATIVE_HAD_UID_KEY })
    // A concurrent auth callback may already have observed a UID.
    hadUid ??= saved.value !== null
  }
  catch (error) { reportFailure(error); return true }
  return hadUid
}

/** Also called with 'pending' BEFORE creating a guest: if saving this durable
 * guard fails we must not mint an identity whose future loss we cannot detect. */
export async function rememberNativeUid(uid: string): Promise<void> {
  if (!IS_NATIVE || writingBlocked) return
  // A callback can observe a real UID while the initial guard read is awaiting
  // Preferences. A guest reservation must never overwrite that confirmed UID.
  if (uid === 'pending' && confirmedUidObserved) return
  const revision = ++uidMarkerRevision
  pendingUidReservation = uid === 'pending' ? revision : null
  if (uid !== 'pending') confirmedUidObserved = true
  hadUid = true
  await serialize(async () => { await (await store()).set({ key: NATIVE_HAD_UID_KEY, value: uid }) })
}


/** Release only this app lifetime's reservation after signInAnonymously has
 * explicitly rejected and Firebase has no current user. A persisted pending
 * marker on a later boot has an unknown outcome and is deliberately retained.
 * Recheck the revision after native reads: an auth callback wins immediately,
 * even when its durable confirmed-UID write is still waiting in the queue. */
export async function releaseFailedNativeUidReservation(): Promise<void> {
  if (!IS_NATIVE || writingBlocked || pendingUidReservation === null) return
  const reservation = pendingUidReservation
  const stillPending = () => !confirmedUidObserved && pendingUidReservation === reservation
    && uidMarkerRevision === reservation && !writingBlocked
  await serialize(async () => {
    if (!stillPending()) return
    const prefs = await store()
    const saved = await prefs.get({ key: NATIVE_HAD_UID_KEY })
    if (!stillPending() || saved.value !== 'pending') return
    await prefs.remove({ key: NATIVE_HAD_UID_KEY })
    // A confirmed callback during removal queues its write after this one and
    // keeps the in-memory guard true until that write reaches Preferences.
    if (stillPending()) {
      pendingUidReservation = null
      hadUid = false
    }
  })
}

/** Stops late save/auth callbacks for this app lifetime. Only confirmed account
 * deletion permits a deliberate new guest on the next visit. Sign-out and a
 * progress reset MUST retain the had-uid guard. No Preferences.clear(): unrelated
 * plugins' data does not belong to this recovery subsystem. */
export async function clearNativePersistence(options: { accountDeleted?: boolean } = {}): Promise<void> {
  if (!IS_NATIVE) return
  writingBlocked = true
  dirty = false
  await serialize(async () => {
    const prefs = await store()
    await prefs.remove({ key: NATIVE_MIRROR_KEY })
    if (options.accountDeleted) {
      await prefs.remove({ key: NATIVE_HAD_UID_KEY })
      hadUid = false
    }
  })
}
